import { useMutation } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { usePowerSync } from '~/powersync/contexts/power-sync/PowerSync';
import { useLivePowerSyncQuery } from '~/powersync/hooks/useLivePowerSyncQuery';
import { db } from '@/lib/local-api';
import type { ChatMessageRecord } from '~/powersync/generated/schema';
import { useCurrentChat } from '~/chat/hooks/useCurrentChat';
import { useChatActions } from '~/chat/hooks/useChatActions';

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  sender: string;
  metadata?: Record<string, unknown> | null;
  createdAt?: Date;
}

/**
 * Reads/sends messages for the conversation currently selected by the route
 * (`/chat/:niceId`, resolved by `useCurrentChat`). Navigating to another chat
 * re-keys the live message query here.
 */
export const useChatWithPowerSync = () => {
  const powerSync = usePowerSync();
  const { chatId } = useCurrentChat();
  const { createNewChat } = useChatActions();

  // Fetch messages from PowerSync with live updates
  const {
    data: messageRecords,
    isLoading: isLoadingMessages,
    error: messagesError,
  } = useLivePowerSyncQuery<ChatMessageRecord>(
    chatId ? db.Chat.queries.findMessages(chatId) : null,
    [chatId],
  );

  // Transform PowerSync records to ChatMessage format
  const messages = useMemo<ChatMessage[]>(() => {
    if (!messageRecords) return [];

    return messageRecords.map((record) => {
      const sender = record.sender || '';
      const role: 'user' | 'assistant' =
        sender === 'HUMAN' ? 'user' : 'assistant';

      return {
        id: record.id,
        role,
        sender,
        metadata:
          typeof record.metadata === 'string'
            ? JSON.parse(record.metadata)
            : record.metadata,
        createdAt: new Date(record.createdAt),
      };
    });
  }, [messageRecords]);

  // Mutation to save messages to PowerSync
  const saveMessageMutation = useMutation({
    mutationFn: async (text: string) => {
      if (!chatId) throw new Error('Chat not initialized');
      return db.Chat.mutations.createMessage(powerSync, chatId, text);
    },
  });

  const sendMessage = useCallback(
    async (text: string) => {
      if (!text.trim() || saveMessageMutation.isPending || !chatId) return;

      try {
        // Save user message to PowerSync
        await saveMessageMutation.mutateAsync(text.trim());
      } catch (err) {
        console.error('Failed to save message:', err);
        throw err;
      }
    },
    [chatId, saveMessageMutation],
  );

  return {
    messages,
    sendMessage,
    loading: !chatId || isLoadingMessages || saveMessageMutation.isPending,
    // The AI is "thinking" while we're waiting for an assistant reply — i.e. a
    // user message is the last in the list (or one is still being saved). The
    // assistant row arriving flips the last role and clears this.
    thinking:
      saveMessageMutation.isPending ||
      (messages.length > 0 && messages[messages.length - 1].role === 'user'),
    error: messagesError || saveMessageMutation.error,
    // Error-recovery affordance: start a fresh conversation (non-destructive).
    clearMessages: createNewChat,
  };
};
